import { CTACard } from '../components/CTACard';
import { NumberedList } from '../components/NumberedList';
import { AccordionStep, ProgressAccordionStepProps } from '../components/AccordionStep';
import { SimplifiedUserRole, ProgressStepStatusEnum } from '@platform/client/schemas';
import { useState } from 'react';
import { PlanUpdateModalProjectPlan } from '../modal-components/PlanUpdateModalProjectPlan';

export const PlanningProjectPlan = ({
  status,
  userType,
  isOpen,
  projectId,
  projectPlanId,
  onToggle,
  isTeam,
}: ProgressAccordionStepProps & {
  projectPlanId?: number | null;
  projectId?: number | null;
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const listItems = getListItems(userType, isTeam);
  const headerContent = getHeaderContent(userType, isTeam);
  return (
    <>
      <AccordionStep title='Planning' status={status} isOpen={isOpen} onToggle={onToggle}>
        <p>🥳 It's a match!</p>
        <p>{headerContent}</p>
        <p className='font-semibold'>Your Planning Steps</p>
        <NumberedList items={listItems} />
        <PlanningCtaCard
          userType={userType}
          isTeam={isTeam}
          setModalIsOpen={setModalIsOpen}
          projectPlanId={projectPlanId}
          status={status}
        />
      </AccordionStep>
      {projectId && (
        <PlanUpdateModalProjectPlan
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          userType={userType}
          isTeam={isTeam ?? false}
        />
      )}
    </>
  );
};

const PlanningCtaCard = ({
  userType,
  isTeam,
  setModalIsOpen,
  projectPlanId,
  status,
}: {
  userType: SimplifiedUserRole;
  isTeam?: boolean;
  setModalIsOpen: (isOpen: boolean) => void;
  projectPlanId?: number | null;
  status: ProgressStepStatusEnum;
}) => {
  const disableCtas = status !== ProgressStepStatusEnum.active_in_progress;
  let description: React.ReactNode;
  switch (userType) {
    case SimplifiedUserRole.PROFESSIONAL:
      description =
        isTeam ?
          <p>
            Complete your section of the Project Plan before the call so your team can focus the
            conversation on decisions, not background. If scheduling is delayed or you need support,{' '}
            <LetUsKnowButton setModalIsOpen={setModalIsOpen} isDisabled={disableCtas} />.
          </p>
        : <p>
            Complete your section of the Project Plan before the call so your planning conversation
            can focus on decisions, not setup. If scheduling is delayed or you need support,{' '}
            <LetUsKnowButton setModalIsOpen={setModalIsOpen} isDisabled={disableCtas} />.
          </p>;
      break;
    case SimplifiedUserRole.REPRESENTATIVE:
      description = (
        <p>
          Complete your section of the Project Plan before the call so your planning conversation
          can focus on decisions, not setup. If scheduling is delayed or you need support,{' '}
          <LetUsKnowButton setModalIsOpen={setModalIsOpen} isDisabled={disableCtas} />.
        </p>
      );
      break;
    default:
      throw new Error('Invalid user type');
  }

  return (
    <CTACard
      title='Ready to get started?'
      description={description}
      ctaButtonProps={{
        size: 'sm',
        children: 'See Project Plan',
        variant: 'primary',
        href: projectPlanId ? `/project-plan/${projectPlanId}` : undefined,
        isDisabled: !projectPlanId,
      }}
    />
  );
};

const LetUsKnowButton = ({
  setModalIsOpen,
  isDisabled,
}: {
  setModalIsOpen: (isOpen: boolean) => void;
  isDisabled: boolean;
}) => (
  <button
    className={isDisabled ? '' : 'blue-link'}
    aria-label='provide plan update'
    onClick={() => setModalIsOpen(true)}
    disabled={isDisabled}
  >
    let us know
  </button>
);

const getHeaderContent = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  switch (userType) {
    case SimplifiedUserRole.PROFESSIONAL:
      return `Great projects start with clarity. In this step, refine the scope together by deciding what is realistic in your timeline and defining clear deliverables or outcomes so ${isTeam ? 'your team feels' : 'you both feel'} confident and ready to move forward.`;
    case SimplifiedUserRole.REPRESENTATIVE:
      return `Great projects start with clarity. In this step, refine the scope ${isTeam ? 'with your team' : 'together'} by deciding what is realistic within the professional's available timeline and defining clear deliverables so you both feel confident and ready to move forward.`;
    default:
      throw new Error('Invalid user type');
  }
};

const getListItems = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  // Shared items
  const listItems = [
    <p key='live-discussion'>
      During the call, review and complete the live discussion section together. Only one person
      needs to enter responses.
    </p>,
    <p key='mark-complete'>
      When you're finished, mark the Project Plan complete to move to the next step. You can use the
      Project Plan as a shared reference throughout the project and update it as things evolve.
    </p>,
  ];

  if (userType === SimplifiedUserRole.REPRESENTATIVE) {
    listItems.unshift(
      <p key='complete-your-part'>
        <strong>Take 10-15 minutes to complete your part of the Project Plan. </strong>
        Your responses help set context for the planning call, and will be visible to the
        professional{isTeam ? 's' : ''}. Reviewing inputs ahead of time helps the conversation focus
        on alignment and decisions, rather than background.
      </p>,
    );
    if (isTeam) {
      listItems.unshift(
        <p key='schedule-call'>
          Email your team of professionals to schedule the planning call. Some teams can cover
          introductions and scoping in one 60-minute call. If helpful, it's okay to start with a
          brief intro call and schedule a follow-up to scope the work.
        </p>,
      );
    } else {
      listItems.unshift(
        <p key='schedule-call'>Email the professional to schedule your planning call.</p>,
      );
    }
  } else if (userType === SimplifiedUserRole.PROFESSIONAL) {
    if (isTeam) {
      listItems.unshift(
        <p key='schedule-call'>
          One person from your team should email the organization to schedule the planning call.
          Some teams can cover introductions and scoping in one 60-minute call. If helpful, it's
          okay to start with a brief intro call and schedule a follow-up to scope the work.
        </p>,
        <p key='prepare-plan'>
          Before the call, each team member should complete their section of the Project Plan to
          come prepared for the conversation.
        </p>,
      );
    } else {
      listItems.unshift(
        <p key='schedule-call'>
          Reach out to the organization by email to schedule your planning call. Most projects can
          be scoped in a single 1 hour call, and you can always schedule more time if needed.
        </p>,
        <p key='prepare-plan'>
          Before the call, complete your section of the Project Plan to come prepared for the
          conversation.
        </p>,
      );
    }
  }
  return listItems;
};
